import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FinanceAPI } from "@/lib/finance-api";
import { InstitutionsAPI, CampusesAPI } from "@/lib/sprint2-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { v: "30d", label: "Last 30 days" },
  { v: "90d", label: "Last 90 days" },
  { v: "ytd", label: "Year to date" },
];

export default function ExpensesDialog({ onClose }) {
  const qc = useQueryClient();
  const [range, setRange] = useState("30d");
  const [showForm, setShowForm] = useState(false);

  const expQ = useQuery({ queryKey: ["expenses", range], queryFn: () => FinanceAPI.listExpenses({ range }) });
  const catQ = useQuery({ queryKey: ["exp-cats"], queryFn: () => FinanceAPI.categories() });

  const removeExp = async (e) => {
    if (!window.confirm(`Delete expense "${e.label}"?`)) return;
    try {
      await FinanceAPI.deleteExpense(e.id);
      toast.success("Expense deleted");
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["fin-summary"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const rows = expQ.data?.expenses || [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="expenses-dialog">
        <DialogHeader>
          <DialogTitle>Expenses · {expQ.data?.total ? `₹${expQ.data.total.toLocaleString("en-IN")}` : "—"}</DialogTitle>
          <DialogDescription>Track operational spend that feeds into the Finance 360° dashboard.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[160px]" data-testid="exp-range"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex-1" />
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="exp-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add expense
          </Button>
        </div>

        {expQ.isLoading ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No expenses in this range.
          </div>
        ) : (
          <div className="border border-border rounded-md divide-y" data-testid="exp-list">
            {rows.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-3 hover:bg-muted/40" data-testid={`exp-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.label}</div>
                  <div className="text-[11px] text-muted-foreground">
                    <span className="capitalize">{e.category}</span>
                    {e.vendor && ` · ${e.vendor}`}
                    {" · "}{new Date(e.spent_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div className="text-right tabular-nums text-sm font-semibold">₹{e.amount.toLocaleString("en-IN")}</div>
                <Button size="icon" variant="ghost" onClick={() => removeExp(e)} data-testid={`exp-del-${e.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>

      {showForm && catQ.data && (
        <CreateExpense
          categories={catQ.data.categories}
          onClose={() => setShowForm(false)}
          onDone={() => {
            setShowForm(false);
            qc.invalidateQueries({ queryKey: ["expenses"] });
            qc.invalidateQueries({ queryKey: ["fin-summary"] });
          }}
        />
      )}
    </Dialog>
  );
}

function CreateExpense({ categories, onClose, onDone }) {
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("");
  const [category, setCategory] = useState(categories[0] || "");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [spentAt, setSpentAt] = useState(new Date().toISOString().slice(0, 10));
  const [vendor, setVendor] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const instQ = useQuery({ queryKey: ["fin-insts"], queryFn: () => InstitutionsAPI.list() });
  const campusQ = useQuery({
    queryKey: ["fin-campuses-form", institutionId],
    queryFn: () => CampusesAPI.list(institutionId ? { institution_id: institutionId } : {}),
    enabled: !!institutionId,
  });

  // Auto-pick first institution when loaded
  if (instQ.data?.length && !institutionId) setInstitutionId(instQ.data[0].id);

  const submit = async () => {
    if (!institutionId || !category || !label.trim() || !amount) {
      toast.error("Fill institution, category, label and amount");
      return;
    }
    setBusy(true);
    try {
      await FinanceAPI.createExpense({
        institution_id: institutionId,
        campus_id: campusId || null,
        category,
        label: label.trim(),
        amount: parseFloat(amount),
        spent_at: new Date(spentAt).toISOString(),
        vendor: vendor.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success("Expense added");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-expense-dialog">
        <DialogHeader><DialogTitle>Add expense</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {instQ.data?.length > 1 && (
            <div>
              <Label className="text-xs">Institution</Label>
              <Select value={institutionId} onValueChange={setInstitutionId}>
                <SelectTrigger data-testid="ce-inst"><SelectValue /></SelectTrigger>
                <SelectContent>{instQ.data.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Campus (optional)</Label>
            <Select value={campusId || "all"} onValueChange={(v) => setCampusId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ce-campus"><SelectValue placeholder="All campuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Institution-wide</SelectItem>
                {(campusQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ce-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="ce-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. AC servicing" data-testid="ce-label" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date spent</Label>
              <Input type="date" value={spentAt} onChange={(e) => setSpentAt(e.target.value)} data-testid="ce-date" />
            </div>
            <div>
              <Label className="text-xs">Vendor (optional)</Label>
              <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="—" data-testid="ce-vendor" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ce-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ce-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
